import React, { useState, useCallback, useRef, useEffect } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { Box, Drawer } from "@mui/material";
import { Header, SideNavigationMenu, Footer } from "../../components";
import { useScreenSize } from "../../utils/media-query";
import { useAuth } from "../../contexts/auth";
import { useUnsavedWork } from "../../contexts/unsavedWork";
import { ROLE_FRONT_DESK } from "../../utils/constants";
import { colors } from "../../pages";
import { useThemeMode } from "../../contexts/theme-mode";

const DRAWER_WIDTH = 250;
const MINI_WIDTH = 60;

const MenuStatus = {
  Closed: 1,
  Opened: 2,
  TemporaryOpened: 3,
};

export default function SideNavInnerToolbar({ title, children }) {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const { isXSmall, isLarge } = useScreenSize();
  const { user } = useAuth();
  const { guardedNavigate } = useUnsavedWork();
  const { isDark } = useThemeMode();
  const sidebarBg = isDark ? colors.charcoal : colors.black;
  const [menuStatus, setMenuStatus] = useState(
    isLarge ? MenuStatus.Opened : MenuStatus.Closed
  );

  useEffect(() => {
    setMenuStatus(isLarge ? MenuStatus.Opened : MenuStatus.Closed);
  }, [isLarge]);

  // Safari drops the paint of an overflow container after a history
  // traversal. A transform nudge makes the restored page visible again.
  useEffect(() => {
    if (navigationType !== "POP") return undefined;
    const node = scrollRef.current;
    if (!node) return undefined;
    const id = window.setTimeout(() => {
      node.style.transform = "translateZ(0)";
      window.requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.style.transform = "";
      });
    }, 0);
    return () => window.clearTimeout(id);
  }, [location.key, navigationType]);

  const menuOpen = menuStatus !== MenuStatus.Closed;
  const compactMode = isLarge && menuStatus === MenuStatus.Closed;
  const desktopWidth = menuOpen ? DRAWER_WIDTH : isXSmall ? 0 : MINI_WIDTH;

  const toggleMenu = useCallback((arg) => {
    const event = arg?.event || arg;
    setMenuStatus((prev) =>
      prev === MenuStatus.Closed ? MenuStatus.Opened : MenuStatus.Closed
    );
    event?.stopPropagation?.();
  }, []);

  const temporaryOpenMenu = useCallback(() => {
    setMenuStatus((prev) =>
      prev === MenuStatus.Closed ? MenuStatus.TemporaryOpened : prev
    );
  }, []);

  const closeMenu = useCallback(() => {
    setMenuStatus(MenuStatus.Closed);
  }, []);

  const onNavigate = useCallback(
    (path) => {
      if (!path) return;
      if (isLarge && menuStatus === MenuStatus.Closed) return;

      // Everything that should happen on a menu click runs only once the
      // unsaved-work guard lets us go, so "Stay" leaves the screen untouched.
      const go = () => {
        navigate(path);
        if (scrollRef.current) {
          scrollRef.current.scrollTop = 0;
        }

        if (!isLarge || menuStatus === MenuStatus.TemporaryOpened) {
          setMenuStatus(MenuStatus.Closed);
        }

        if (
          user?.userRoles?.includes(ROLE_FRONT_DESK) &&
          (path === "/scheduler" || path === "/till")
        ) {
          setMenuStatus(MenuStatus.Closed);
        }
      };

      // Clicking the item for the page already shown is not a departure:
      // the route does not remount, so there is nothing to lose and no
      // reason to offer "Leave and lose changes".
      if (path.split("?")[0] === location.pathname) {
        go();
      } else {
        guardedNavigate(go);
      }
    },
    [guardedNavigate, location.pathname, navigate, menuStatus, isLarge, user]
  );

  const menu = (
    <SideNavigationMenu
      compactMode={compactMode}
      onNavigate={onNavigate}
      openMenu={temporaryOpenMenu}
      onMenuToggle={toggleMenu}
      showMenuToggle={!isXSmall}
    />
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
      }}
    >
      {isLarge ? (
        <Box
          sx={{
            width: desktopWidth,
            flexShrink: 0,
            height: "100%",
            overflow: "hidden",
            transition: "width 0.2s ease",
            bgcolor: sidebarBg,
            borderRight: isDark ? "none" : `1px solid ${colors.divider}`,
          }}
        >
          {menu}
        </Box>
      ) : (
        <Drawer
          variant="temporary"
          open={menuOpen}
          onClose={closeMenu}
          ModalProps={{
            // A kept-mounted hidden layer stays over the page in Safari
            // and paints the outlet blank after Back.
            keepMounted: false,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              bgcolor: sidebarBg,
              borderRight: "none",
            },
          }}
        >
          {menu}
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          height: "100%",
        }}
      >
        <Header
          menuToggleEnabled={!isLarge}
          toggleMenu={toggleMenu}
        />
        <Box
          ref={scrollRef}
          className="layout-body with-footer"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box className="content" sx={{ flexGrow: 1 }}>
            {React.Children.map(children, (item) =>
              item?.type !== Footer ? item : null
            )}
          </Box>
          <Box className="content-block">
            {React.Children.map(children, (item) =>
              item?.type === Footer ? item : null
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
